import Header from "@components/Header/Header";
import { useLocation, useNavigate } from "react-router";
import { toCurrencyFormat } from "@lib/utils";
import { useTranslation } from "react-i18next";
import PrimaryButton from "@components/PrimaryButton/PrimaryButton";
import ExpandableStepList from "@components/ExpandableStepList/ExpandableStepList";
import {
    Container,
    ContentContainer,
    ButtonGroup,
    BankLogoContainer,
    BankLogo,
    InstructionsContainer,
    JIDRCurrency,
    BankLogoNameContainer,
    BankLogoRow,
    BankInfoText,
    BankDisplayName,
    BankCodeText,
    TransferPromptText,
    TransferPromptBold,
    ExpirationDateContainer,
    ExpirationDateText,
    ExpirationDateBold,
    VirtualNumberBoxContainer,
    VirtualNumberBoxLabel,
    VirtualNumberBoxRow,
    VirtualAccountNumberText,
    TotalAmountText,
    EquivalentText,
    VirtualNumberDivider,
    Spacer
} from "./TopUpBankVirtualAccount.styles";
import useSupportedVirtualAccountInstructions from "@hooks/useSupportedVirtualAccountInstructions";
import LoadingModal from "@components/LoadingModal/LoadingModal";
import CopyButton from "@components/CopyButton/CopyButton";

const BankLogoAndName = ({
    displayName,
    code,
    iconUrl,
}: {
    displayName: string,
    code: string,
    iconUrl: string,
}) => {
    const { t } = useTranslation();
    return (
        <BankLogoNameContainer>
            <BankLogoRow>
                <BankLogoContainer>
                    <BankLogo
                        src={iconUrl}
                        alt="Bank logo"
                    />
                </BankLogoContainer>
                <JIDRCurrency />
            </BankLogoRow>
            <BankInfoText>
                <BankDisplayName>
                    {t('topUpBank.virtualAccount.bankLogoAndName.virtualAccount', { displayName })}
                </BankDisplayName>
                <BankCodeText>
                    {code}
                </BankCodeText>
            </BankInfoText>
        </BankLogoNameContainer>
    )
}

const TransferPrompt = ({
    amount
}: {
    amount: number
}) => {
    const { t } = useTranslation();
    return (
        <TransferPromptText>
            {t('topUpBank.virtualAccount.transferPrompt.text')} <TransferPromptBold>Rp{toCurrencyFormat(amount)}</TransferPromptBold> {t('topUpBank.virtualAccount.transferPrompt.suffix')}
        </TransferPromptText>
    )
}

const ExpirationDate = ({ expirationDate }: { expirationDate: string }) => {
    const { t } = useTranslation();
    const formattedDate = `${new Date(expirationDate).toLocaleDateString('en-GB', {
        weekday: 'long',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    })} at ${new Date(expirationDate).toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    })} (GMT${(() => {
        const offset = -new Date(expirationDate).getTimezoneOffset() / 60;
        return offset >= 0 ? `+${offset}` : offset;
    })()})`;

    return (
        <ExpirationDateContainer>
            <ExpirationDateText>
                {t('topUpBank.virtualAccount.expirationDate.before')} <ExpirationDateBold>{formattedDate}</ExpirationDateBold>
            </ExpirationDateText>
        </ExpirationDateContainer>
    )
}

const VirtualNumberBox = ({
    accountNumber,
    amount
}: {
    accountNumber: string,
    amount: number
}) => {
    const { t } = useTranslation();

    return (
        <VirtualNumberBoxContainer>
            <VirtualNumberBoxLabel>
                {t('topUpBank.virtualAccount.virtualNumberBox.virtualAccountNumber')}
            </VirtualNumberBoxLabel>
            <VirtualNumberBoxRow>
                <VirtualAccountNumberText>
                    {accountNumber}
                </VirtualAccountNumberText>
                <CopyButton stringToCopy={accountNumber} onClick={() => { }} />
            </VirtualNumberBoxRow>
            <VirtualNumberDivider />
            <VirtualNumberBoxLabel>
                {t('topUpBank.virtualAccount.virtualNumberBox.totalAmount')}
            </VirtualNumberBoxLabel>
            <VirtualNumberBoxRow>
                <TotalAmountText>
                    Rp{toCurrencyFormat(amount)}
                </TotalAmountText>
                <EquivalentText>
                    {t('topUpBank.virtualAccount.virtualNumberBox.equivalent', { amount: toCurrencyFormat(amount) })}
                </EquivalentText>
            </VirtualNumberBoxRow>
        </VirtualNumberBoxContainer>
    )
}

const CustomSpacer = ({ space }: { space: number }) => {
    return <Spacer space={space} />
}

const TopUpBankVirtualAccount = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { bank, amount, virtualAccount, expiryDate } = useLocation().state
    const {
        data: instructions,
        isLoading: instructionsLoading,
        error: instructionsError
    } = useSupportedVirtualAccountInstructions()

    return (
        <Container>
            <Header title={t('topUpBank.virtualAccount.header')} showBackButton onBackClick={() => navigate('/top-up-bank-input-amount', { state: { bank, amount } })} />
            <LoadingModal visible={instructionsLoading} />
            <ContentContainer>
                <BankLogoAndName
                    iconUrl={bank.iconUrl}
                    displayName={bank.name}
                    code={bank.id}
                />
                <CustomSpacer space={12} />
                <TransferPrompt
                    amount={amount}
                />
                <CustomSpacer space={8} />
                <ExpirationDate
                    expirationDate={expiryDate}
                />
                <CustomSpacer space={20} />
                <VirtualNumberBox
                    accountNumber={virtualAccount}
                    amount={amount}
                />
                <CustomSpacer space={20} />
                {
                    instructions &&
                    !instructionsError &&
                    instructions[bank.id] &&
                    instructions[bank.id].length > 0 &&
                    <InstructionsContainer>
                        {instructions[bank.id].map((instruction) =>
                            <ExpandableStepList
                                title={instruction.title}
                                steps={instruction.content}
                            />
                        )}
                    </InstructionsContainer>
                }
            </ContentContainer>
            <ButtonGroup>
                <PrimaryButton
                    variant="outline"
                    onClick={() => navigate('/top-up-bank-input-amount', { state: { bank, amount } })}
                >
                    {t('topUpBank.virtualAccount.buttons.reviseAmount')}
                </PrimaryButton>
                <PrimaryButton
                    onClick={() => navigate('/home')}
                >
                    {t('topUpBank.virtualAccount.buttons.done')}
                </PrimaryButton>
            </ButtonGroup>
        </Container>
    )
};

export default TopUpBankVirtualAccount;